import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IconChevronDown, IconPencil, IconPlus, IconSearch, IconTrash, IconUsersGroup } from '@tabler/icons-react';
import type { Group } from '../../../../entities/group';
import type { User } from '../../../../entities/user';
import { pb } from '../../../../shared/api/pocketbase';
import { getManagerColor, getManagerInitials } from '../../../../shared/lib/utils';
import { logAction } from '../../../../shared/lib/userLogger';
import { Button } from '../../../../shared/ui/Button';
import { GroupModal } from '../../../../features/admin-group-form';

const pluralGroups = (n: number) => {
  if (n === 1) return '1 группа';
  if (n < 5) return `${n} группы`;
  return `${n} групп`;
}

const pluralMembers = (n: number) => {
  if (n === 1) return 'менеджер';
  if (n < 5) return 'менеджера';
  return 'менеджеров';
}

const SEARCH_THRESHOLD = 8;

interface GroupsSectionProps {
  /** Только просмотр: скрывает создание/редактирование/удаление (для менеджеров) */
  readOnly?: boolean;
  /** Показывать только группы, где этот пользователь тимлид или участник */
  scopeUserId?: string;
}

const GroupsSection: React.FC<GroupsSectionProps> = ({ readOnly = false, scopeUserId }) => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState<{ mode: 'add' | 'edit'; group?: Group } | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState('');
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());
  const [sectionSearch, setSectionSearch] = useState<Record<string, string>>({});

  const toggleOpen = (id: string) => setOpenIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const setSectionQ = (key: string, q: string) =>
    setSectionSearch(prev => ({ ...prev, [key]: q }));

  const handleDelete = async (id: string) => {
    setDeleting(true);
    try {
      const groupName = groups.find(g => g.id === id)?.name || id;
      await pb.collection('groups').delete(id);
      logAction(`Deleted group: ${groupName}`, 'REMOVE');
      setConfirmDeleteId(null);
      loadGroups();
    } finally {
      setDeleting(false);
    }
  };

  const loadGroups = useCallback(() => {
    setLoading(true);
    pb.collection('groups')
      .getFullList<Group>({
        sort: '-created',
        filter: scopeUserId ? `managers ~ "${scopeUserId}" || usersIds ~ "${scopeUserId}"` : undefined,
        expand: 'managers,usersIds,providerId,templateSmsId',
        fields: 'id,name,managers,usersIds,providerId,templateSmsId,created,updated,expand',
      })
      .then(setGroups)
      .catch(() => setGroups([]))
      .finally(() => setLoading(false));
  }, [scopeUserId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter(g => {
      const name = g.name || g.expand?.managers?.map(m => m.name || m.email).join(' ') || '';
      return name.toLowerCase().includes(q);
    });
  }, [groups, search]);

  useEffect(() => {
    loadGroups();
    if (readOnly) return;
    pb.collection('users')
      .getFullList<User>({ sort: 'name', fields: 'id,name,email,role,created' })
      .then(setUsers)
      .catch(() => setUsers([]));
  }, [loadGroups, readOnly]);

  return (
    <div className="adm-section">
      <div className="adm-section-header adm-section-header-row">
        <div>
          <h2 className="adm-section-title">Группы</h2>
          <p className="adm-section-sub">{loading ? 'Загрузка...' : pluralGroups(groups.length)}</p>
        </div>
        <div className="adm-ut-toolbar">
          <div className="adm-input-wrap" style={{ width: 220 }}>
            <span className="adm-input-icon"><IconSearch size={15} stroke={2} /></span>
            <input
              className="adm-input"
              placeholder="Поиск по группе..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          {!readOnly && (
            <Button variant="adm-add" type="button" onClick={() => setModal({ mode: 'add' })}>
              <IconPlus size={15} stroke={2.5} />Создать группу
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="adm-loading">Загрузка...</div>
      ) : (
        <div className="adm-groups-list">
          {filtered.map(g => {
            const managers = g.expand?.managers ?? [];
            const members = g.expand?.usersIds ?? [];
            const isOpen = openIds.has(g.id);
            const title = g.name || (managers.length > 0
              ? managers.map(m => m.name || m.email).join(', ')
              : `Группа ${g.id.slice(-4)}`);
            const allPeople = [...managers, ...members];
            const stackShow = allPeople.slice(0, 4);
            const stackRest = allPeople.length - stackShow.length;
            const onlyOne = (managers.length > 0) !== (members.length > 0);

            return (
              <div key={g.id} className={`adm-group-card${isOpen ? ' open' : ''}`}>
                <div className="adm-group-header" onClick={() => toggleOpen(g.id)}>
                  <div className="adm-group-icon"><IconUsersGroup size={16} stroke={2} /></div>
                  <div className="adm-group-meta">
                    <div className="adm-group-title">{title}</div>
                    <div className="adm-group-counts">
                      {managers.length} {managers.length === 1 ? 'тимлид' : 'тимлида'} · {members.length} {pluralMembers(members.length)}
                    </div>
                  </div>
                  {stackShow.length > 0 && (
                    <div className="adm-group-stack">
                      {stackShow.map(m => {
                        const ini = getManagerInitials(m.name || m.email, m.email);
                        const col = getManagerColor(m.id || m.name || m.email);
                        return (
                          <div key={m.id} className="user-av" style={{ background: col, width: 26, height: 26, fontSize: 10, flexShrink: 0 }} title={m.name || m.email}>{ini}</div>
                        );
                      })}
                      {stackRest > 0 && <div className="adm-group-stack-more">+{stackRest}</div>}
                    </div>
                  )}
                  {!readOnly && (
                  <div className="adm-group-actions" onClick={e => e.stopPropagation()}>
                    {confirmDeleteId === g.id ? (
                      <div className="adm-ut-confirm">
                        <Button variant="adm-cancel" size="sm" type="button" onClick={() => setConfirmDeleteId(null)}>Отмена</Button>
                        <Button variant="danger" size="sm" type="button" disabled={deleting} onClick={() => handleDelete(g.id)}>
                          {deleting ? '...' : 'Удалить'}
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Button variant="adm-edit" type="button" title="Редактировать"
                          onClick={() => setModal({ mode: 'edit', group: g })}>
                          <IconPencil size={14} stroke={2} />
                        </Button>
                        <Button variant="adm-delete" type="button" title="Удалить"
                          onClick={() => setConfirmDeleteId(g.id)}>
                          <IconTrash size={14} stroke={2} />
                        </Button>
                      </>
                    )}
                  </div>
                  )}
                  <IconChevronDown size={16} stroke={2} className={`adm-group-chevron${isOpen ? ' open' : ''}`} />
                </div>

                {isOpen && allPeople.length > 0 && (
                  <div className={`adm-group-body${onlyOne ? ' single' : ''}`}>
                    {([
                      { key: `${g.id}:leads`, label: 'Тимлиды', list: managers },
                      { key: `${g.id}:members`, label: 'Менеджеры', list: members },
                    ] as const).map(({ key, label, list }) => {
                      if (!list.length) return null;
                      const q = (sectionSearch[key] ?? '').toLowerCase();
                      const visible = q
                        ? list.filter(m => (m.name || m.email).toLowerCase().includes(q))
                        : list;
                      return (
                        <div key={key} className="adm-group-section">
                          <div className="adm-group-section-header">
                            <span className="adm-group-section-label">{label}</span>
                            <span className="adm-group-section-count">{list.length}</span>
                          </div>
                          {list.length > SEARCH_THRESHOLD && (
                            <div className="adm-group-section-search">
                              <IconSearch size={12} stroke={2} />
                              <input
                                placeholder="Поиск..."
                                value={sectionSearch[key] ?? ''}
                                onChange={e => setSectionQ(key, e.target.value)}
                                onClick={e => e.stopPropagation()}
                              />
                            </div>
                          )}
                          <div className="adm-group-av-grid">
                            {visible.map(m => {
                              const ini = getManagerInitials(m.name || m.email, m.email);
                              const col = getManagerColor(m.id || m.name || m.email);
                              const displayName = m.name || m.email;
                              return (
                                <div key={m.id} className="adm-group-av-cell" title={m.name || m.email}>
                                  <div className="user-av" style={{ background: col, width: 34, height: 34, fontSize: 12, flexShrink: 0 }}>{ini}</div>
                                  <span className="adm-group-av-cell-name">{displayName}</span>
                                </div>
                              );
                            })}
                            {visible.length === 0 && (
                              <span className="adm-group-av-empty">Не найдено</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          {!loading && filtered.length === 0 && (
            <div className="adm-empty">{search ? 'Ничего не найдено' : 'Группы не созданы'}</div>
          )}
        </div>
      )}

      {modal && (
        <GroupModal
          mode={modal.mode}
          initial={modal.group ?? null}
          allUsers={users}
          onClose={() => setModal(null)}
          onSaved={loadGroups}
        />
      )}
    </div>
  );
};

export { GroupsSection };
